import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Scale, Coins, Heart, Dumbbell, 
  Sparkles, Unlock, Target, Gift, X,
  ArrowRight, MessageCircle, AudioWaveform,
  Moon, Users
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * The Reclaim Map - Premium Universe
 * 
 * A living, guided ecosystem - not a menu.
 * Hierarchy: Center → Inner Ring → Middle Ring → Outer Ring
 * 
 * Design Principles:
 * - Calm, spacious, intentional
 * - Nothing static, nothing distracting
 * - Quiet luxury + living depth
 */

// =============================================================
// PILLAR DEFINITIONS - With hierarchy, colors, and final copy
// =============================================================

const PILLARS = [
  // ═══════════════════════════════════════════════════════════
  // CENTER - Core Engine (1.35x size)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'private-suite',
    name: 'Private Suite',
    ring: 'center',
    icon: AudioWaveform,
    // Deep luminous violet - personal, powerful, premium
    color: '#9333EA',
    glowColor: 'rgba(147, 51, 234, 0.5)',
    haloColor: 'rgba(147, 51, 234, 0.2)',
    route: '/private-suite',
    status: 'active',
    position: { x: 38, y: 42 },  // Centered: shifted left and up
    sizeMultiplier: 1.35,
    body: `This is your space

Where everything becomes personal

Your voice
Your focus
Your intention

If you want something created just for you…
this is where it happens`,
    cta: 'Enter Your Space',
    exceliorLine: "Let's create something just for you"
  },

  // ═══════════════════════════════════════════════════════════
  // INNER RING - Daily Use / Entry Layer (1.0x size)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'passion',
    name: 'Reclaim Our Passion',
    ring: 'inner',
    icon: Flame,
    // Warm amber / ember orange - energy, movement
    color: '#F59E0B',
    glowColor: 'rgba(245, 158, 11, 0.45)',
    haloColor: 'rgba(245, 158, 11, 0.15)',
    route: '/challenges',
    status: 'active',
    position: { x: 14, y: 28 },  // Shifted left and up
    sizeMultiplier: 1.0,
    body: `This is where you reconnect
with what lights you up

Simple challenges… real movement

If you feel stuck or distracted…
start here`,
    cta: 'Enter',
    exceliorLine: "Start light… let movement bring you back"
  },
  {
    id: 'balance',
    name: 'Reclaim Our Balance',
    ring: 'inner',
    icon: Scale,
    // Soft grounded green - calm, centre
    color: '#4A6741',
    glowColor: 'rgba(74, 103, 65, 0.45)',
    haloColor: 'rgba(74, 103, 65, 0.15)',
    route: '/balance',
    status: 'active',
    position: { x: 38, y: 14 },  // Top center
    sizeMultiplier: 1.0,
    body: `This brings you back to centre

Small resets and micro journeys
used throughout your day

If you feel overwhelmed or scattered…
return here`,
    cta: 'Enter',
    exceliorLine: "You don't need to do much… just return"
  },
  {
    id: 'peace',
    name: 'Reclaim Our Peace',
    ring: 'inner',
    icon: Moon,
    // Deep calm indigo / soft silver - stillness, depth
    color: '#6366F1',
    glowColor: 'rgba(99, 102, 241, 0.35)',
    haloColor: 'rgba(99, 102, 241, 0.12)',
    route: '/peace',
    status: 'coming',
    position: { x: 38, y: 28 },  // Between Balance and Private Suite
    sizeMultiplier: 1.05,
    // Special visual flags for Peace
    isPeace: true,  // Triggers slower, softer animation
    body: `This is the still point

Not doing
Not becoming

Just returning

When you need to drop out of noise…
settle here`,
    cta: 'Enter',
    exceliorLine: "There is nothing to figure out here"
  },
  {
    id: 'studio',
    name: 'Studio',
    ring: 'inner',
    icon: Gift,
    // Magenta-purple - connection, sharing
    color: '#DB2777',
    glowColor: 'rgba(219, 39, 119, 0.45)',
    haloColor: 'rgba(219, 39, 119, 0.15)',
    route: '/studio',
    status: 'coming',
    position: { x: 62, y: 22 },  // Right side, inner ring
    sizeMultiplier: 1.0,
    body: `This is where you share

A moment… a challenge… a shift

Not to convince…
but to invite`,
    cta: 'Enter',
    exceliorLine: "Share what you feel… not what you think"
  },

  // ═══════════════════════════════════════════════════════════
  // MIDDLE RING - Growth Layer (0.95x size)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'abundance',
    name: 'Reclaim Our Abundance',
    ring: 'middle',
    icon: Coins,
    // Gold with deep emerald - richness, expansion
    color: '#CA8A04',
    glowColor: 'rgba(202, 138, 4, 0.4)',
    haloColor: 'rgba(202, 138, 4, 0.12)',
    route: '/abundance',
    status: 'active',
    position: { x: 10, y: 46 },  // Left side, shifted up
    sizeMultiplier: 0.95,
    body: `This is where you expand
what feels possible

Not by chasing…
but by aligning

If you're ready for more…
begin here`,
    cta: 'Enter',
    exceliorLine: "This isn't about chasing… just aligning"
  },
  {
    id: 'strength',
    name: 'Reclaim Our Strength',
    ring: 'middle',
    icon: Dumbbell,
    // Deep blue / steel blue - stability, power
    color: '#3B82F6',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    haloColor: 'rgba(59, 130, 246, 0.12)',
    route: '/strength',
    status: 'coming',
    position: { x: 66, y: 46 },  // Right side, shifted up
    sizeMultiplier: 0.95,
    body: `This is where you build strength
from the inside out

Focus, discipline, and direction

If you're ready to step forward…
begin here`,
    cta: 'Enter',
    exceliorLine: "Focus your energy… and it will respond"
  },
  {
    id: 'wellbeing',
    name: 'Reclaim Our Well Being',
    ring: 'middle',
    icon: Heart,
    // Soft teal / aqua - healing, openness
    color: '#14B8A6',
    glowColor: 'rgba(20, 184, 166, 0.4)',
    haloColor: 'rgba(20, 184, 166, 0.12)',
    route: '/wellbeing',
    status: 'coming',
    position: { x: 38, y: 60 },  // Bottom center, shifted up
    sizeMultiplier: 0.95,
    body: `This is where you go deeper

With guidance and support
that meets you where you are

If you're ready to explore further…
this will hold you`,
    cta: 'Enter',
    exceliorLine: "You don't have to go deeper alone"
  },

  // ═══════════════════════════════════════════════════════════
  // OUTER RING - Advanced / Identity Layer (0.9x size)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'radiance',
    name: 'Reclaim Our Radiance',
    ring: 'outer',
    icon: Sparkles,
    // Soft rose / rose-gold pink - beauty, expression
    color: '#F472B6',
    glowColor: 'rgba(244, 114, 182, 0.35)',
    haloColor: 'rgba(244, 114, 182, 0.1)',
    route: '/radiance',
    status: 'coming',
    position: { x: 8, y: 66 },  // Far left, shifted up
    sizeMultiplier: 0.9,
    body: `This is expression

What you carry…
and how you choose to show up

A reflection of your energy in form`,
    cta: 'Enter',
    exceliorLine: "This is how you choose to express what you carry"
  },
  {
    id: 'liberation',
    name: 'Reclaim Our Liberation',
    ring: 'outer',
    icon: Unlock,
    // Soft white / pearl glow - freedom, spaciousness
    color: '#E5E7EB',
    glowColor: 'rgba(229, 231, 235, 0.35)',
    haloColor: 'rgba(229, 231, 235, 0.1)',
    route: '/liberation',
    status: 'locked',
    position: { x: 38, y: 76 },  // Bottom center, shifted up
    sizeMultiplier: 0.9,
    body: `This opens after deeper work

It's not something you enter…
it's something you arrive in

A space of freedom, from within`,
    cta: 'Enter',
    exceliorLine: "This opens when you're ready… not before"
  },
  {
    id: 'alignment',
    name: 'Alignment',
    subtitle: 'Invitation Only',
    ring: 'outer',
    icon: Target,
    // Deep violet / indigo - higher refinement
    color: '#6366F1',
    glowColor: 'rgba(99, 102, 241, 0.35)',
    haloColor: 'rgba(99, 102, 241, 0.1)',
    route: '/alignment',
    status: 'invitation',
    position: { x: 68, y: 66 },  // Far right, shifted up
    sizeMultiplier: 0.9,
    body: `This is refinement

For those stepping into
deeper alignment and leadership

Access comes when you are ready`,
    cta: 'Enter',
    exceliorLine: "Refinement begins here"
  },

  // ═══════════════════════════════════════════════════════════
  // EXPANSION LAYER - Side node, not part of core journey
  // ═══════════════════════════════════════════════════════════
  {
    id: 'zones',
    name: 'Zones',
    subtitle: 'Tailored Spaces',
    ring: 'expansion',
    icon: Users,
    // Soft neutral / silver - accessible, inviting
    color: '#94A3B8',
    glowColor: 'rgba(148, 163, 184, 0.3)',
    haloColor: 'rgba(148, 163, 184, 0.08)',
    route: '/zones',
    status: 'coming',
    position: { x: 78, y: 42 },  // Far right, off the main path
    sizeMultiplier: 0.85,
    body: `Tailored spaces for different journeys

Teen Zone
Parent Zone
Networkers Zone
And more coming…

Find where you belong`,
    cta: 'Explore',
    exceliorLine: "Different paths… same destination"
  }
];

// Excelior whispers
const WHISPERS = [
  "You don't need to explore everything…",
  "Start where you feel drawn",
  "There is no rush",
  "Begin where you feel called",
  "Trust your pace"
];

// Feelings for guided mode
const FEELINGS = [
  { id: 'overwhelmed', label: 'Overwhelmed', pillar: 'balance' },
  { id: 'stuck', label: 'Stuck', pillar: 'passion' },
  { id: 'low', label: 'Low energy', pillar: 'passion' },
  { id: 'curious', label: 'Curious', pillar: null },
  { id: 'ready', label: 'Ready to grow', pillar: 'abundance' }
];

// =============================================================
// CELESTIAL BACKGROUND - Stars, Nebula & Floating Particles
// =============================================================

const CelestialBackground = ({ pillars }) => {
  // Far layer: Tiny twinkling stars
  const stars = useMemo(() => 
    [...Array(80)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 0.5 + Math.random() * 1.5,
      twinkleDuration: 2 + Math.random() * 3,
      delay: Math.random() * 5,
      opacity: 0.15 + Math.random() * 0.25
    })), []
  );

  // Mid layer: Floating particles (denser near nodes)
  const particles = useMemo(() => 
    [...Array(50)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 1.5,
      duration: 20 + Math.random() * 15,
      delay: Math.random() * 10,
      opacity: 0.08 + Math.random() * 0.15
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Far layer: Twinkling stars */}
      {stars.map(s => (
        <motion.div
          key={`star-${s.id}`}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.size,
            height: s.size,
            boxShadow: '0 0 3px rgba(255,255,255,0.5)'
          }}
          animate={{
            opacity: [s.opacity * 0.3, s.opacity, s.opacity * 0.3],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: s.twinkleDuration,
            delay: s.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
      
      {/* Mid layer: Floating particles */}
      {particles.map(p => (
        <motion.div
          key={`particle-${p.id}`}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: 'rgba(255,255,255,0.8)',
            boxShadow: '0 0 4px rgba(255,255,255,0.3)'
          }}
          animate={{
            y: [-15, 15, -15],
            x: [-8, 8, -8],
            opacity: [p.opacity * 0.5, p.opacity, p.opacity * 0.5]
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
      
      {/* Near layer: Node aura hazes - soft colored nebula effect */}
      {pillars.map(pillar => (
        <motion.div
          key={`nebula-${pillar.id}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${pillar.position.x}%`,
            top: `${pillar.position.y}%`,
            width: 200 * pillar.sizeMultiplier,
            height: 200 * pillar.sizeMultiplier,
            transform: 'translate(-50%, -50%)',
            background: `radial-gradient(circle, ${pillar.glowColor.replace('0.4', '0.06').replace('0.45', '0.06').replace('0.35', '0.05')} 0%, transparent 70%)`,
            filter: 'blur(30px)'
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 8 + Math.random() * 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Legacy export for backward compatibility
const FloatingParticles = () => <CelestialBackground pillars={PILLARS} />;

// =============================================================
// NODE COMPONENT - Living energy sphere with identity glow
// =============================================================

const MapNode = ({ 
  pillar, 
  isSelected, 
  isHighlighted,
  onSelect,
  isFocusMode 
}) => {
  const Icon = pillar.icon;
  const isLocked = pillar.status === 'locked' || pillar.status === 'invitation';
  const isComingSoon = pillar.status === 'coming';
  const isCenter = pillar.ring === 'center';
  const isPeace = pillar.isPeace === true;  // Special calm node
  const isExpansion = pillar.ring === 'expansion';  // Side nodes like Zones
  
  // Base size for nodes (reduced for better fit)
  const baseSize = 56; // px (reduced from 70 for less clutter)
  const size = baseSize * pillar.sizeMultiplier;
  
  // Touch area is larger than visual for mobile ease
  const touchSize = size * 1.4;
  
  // Dimming in focus mode
  const dimmed = isFocusMode && !isSelected;
  
  // Breathing animation timing
  // Peace: slower (10-12s), deeper feel
  // Expansion: subtle (8s)
  // Normal: 6-8 seconds
  const breathDuration = isPeace ? 10 + (PILLARS.indexOf(pillar) % 3) 
    : isExpansion ? 8 
    : 6 + (PILLARS.indexOf(pillar) % 3);

  return (
    <motion.button
      onClick={() => onSelect(pillar)}
      className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
      style={{ 
        left: `${pillar.position.x}%`, 
        top: `${pillar.position.y}%`,
        zIndex: isSelected ? 20 : isCenter ? 15 : 10,
        // Expanded touch area
        width: touchSize,
        height: touchSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: isSelected ? 1.08 : 1,
        opacity: dimmed ? 0.3 : (isLocked ? 0.6 : 1)
      }}
      whileHover={{ scale: isSelected ? 1.08 : 1.05 }}
      transition={{ 
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: PILLARS.indexOf(pillar) * 0.08
      }}
      data-testid={`pillar-${pillar.id}`}
    >
      {/* IDENTITY GLOW - Resting colour aura (always visible, very subtle) */}
      {/* Peace node: deeper, slower, more gravitational */}
      <motion.div 
        className="absolute rounded-full pointer-events-none"
        style={{ 
          width: size * (isPeace ? 3.5 : 3),
          height: size * (isPeace ? 3.5 : 3),
          background: `radial-gradient(circle, ${pillar.color}${isPeace ? '12' : '15'} 0%, ${pillar.color}08 40%, transparent 70%)`,
          filter: isPeace ? 'blur(30px)' : 'blur(20px)'
        }}
        animate={{
          scale: isSelected ? 1.4 : isPeace ? [1, 1.05, 1] : [1, 1.1, 1],
          opacity: isSelected ? 0.8 : isPeace ? [0.4, 0.55, 0.4] : [0.5, 0.7, 0.5]
        }}
        transition={{
          duration: isSelected ? 0.3 : breathDuration,
          repeat: isSelected ? 0 : Infinity,
          ease: isPeace ? "linear" : "easeInOut"
        }}
      />
      
      {/* Outer halo - atmospheric ring */}
      <motion.div 
        className="absolute rounded-full pointer-events-none"
        style={{ 
          width: size * 2.2,
          height: size * 2.2,
          background: `radial-gradient(circle, ${pillar.haloColor} 0%, transparent 70%)`,
        }}
        animate={{
          scale: isSelected ? 1.3 : isPeace ? [1, 1.04, 1] : [1, 1.08, 1],
          opacity: isSelected ? 0.9 : isPeace ? [0.25, 0.4, 0.25] : [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: isSelected ? 0.3 : breathDuration,
          repeat: isSelected ? 0 : Infinity,
          ease: isPeace ? "linear" : "easeInOut"
        }}
      />
      
      {/* Inner glow - core energy */}
      <motion.div 
        className="absolute rounded-full blur-xl pointer-events-none"
        style={{ 
          width: size * 1.5,
          height: size * 1.5,
          backgroundColor: pillar.glowColor,
        }}
        animate={{
          scale: isSelected ? 1.5 : isPeace ? [1, 1.06, 1] : [1, 1.12, 1],
          opacity: isSelected ? 0.95 : isPeace ? [0.3, 0.5, 0.3] : [0.4, 0.65, 0.4]
        }}
        transition={{
          duration: isSelected ? 0.3 : breathDuration * 0.8,
          repeat: isSelected ? 0 : Infinity,
          ease: isPeace ? "linear" : "easeInOut"
        }}
      />
      
      {/* Center extra glow pulse - only for Private Suite */}
      {isCenter && (
        <motion.div 
          className="absolute rounded-full blur-2xl pointer-events-none"
          style={{ 
            width: size * 2.5,
            height: size * 2.5,
            backgroundColor: pillar.glowColor,
          }}
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.15, 0.35, 0.15]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      {/* Node sphere - main visual with parallax float */}
      <motion.div 
        className="relative rounded-full flex items-center justify-center overflow-hidden"
        style={{ 
          width: size,
          height: size,
          background: `radial-gradient(circle at 30% 30%, 
            rgba(255,255,255,0.18) 0%, 
            rgba(255,255,255,0.06) 40%,
            rgba(0,0,0,0.15) 100%)`,
          border: `1.5px solid ${isSelected ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.18)'}`,
          boxShadow: isSelected 
            ? `0 0 40px ${pillar.glowColor}, 0 0 80px ${pillar.glowColor.replace('0.4', '0.2')}, inset 0 0 25px rgba(255,255,255,0.12)`
            : `0 0 20px ${pillar.glowColor.replace('0.4', '0.15')}, inset 0 0 15px rgba(255,255,255,0.06)`
        }}
        animate={{
          y: isSelected ? 0 : [0, -4, 0],
          x: isSelected ? 0 : [0, 2, 0]
        }}
        transition={{
          duration: breathDuration * 1.2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {/* Inner particle effect for center node */}
        {isCenter && (
          <div className="absolute inset-0 overflow-hidden rounded-full">
            {[...Array(7)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-white/40"
                style={{
                  left: `${15 + Math.random() * 70}%`,
                  top: `${15 + Math.random() * 70}%`
                }}
                animate={{
                  x: [-6, 6, -6],
                  y: [-6, 6, -6],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{
                  duration: 3 + i * 0.5,
                  repeat: Infinity,
                  delay: i * 0.4
                }}
              />
            ))}
          </div>
        )}
        
        <Icon 
          className="relative z-10 transition-all duration-300"
          style={{ 
            width: size * 0.4,
            height: size * 0.4,
            color: isSelected || isCenter ? pillar.color : 'rgba(255,255,255,0.75)',
            filter: isSelected ? `drop-shadow(0 0 8px ${pillar.color})` : 'none'
          }}
        />
      </motion.div>
      
      {/* Label - visible on hover or for center */}
      <motion.div 
        className="absolute top-full mt-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-center pointer-events-none"
        initial={{ opacity: 0, y: -5 }}
        animate={{ 
          opacity: isSelected || isCenter ? 1 : 0,
          y: isSelected || isCenter ? 0 : -5
        }}
        whileHover={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <p className="text-sm text-white/90 font-medium tracking-wide">
          {pillar.name}
        </p>
        {pillar.subtitle && (
          <p className="text-[10px] text-white/40 mt-0.5">{pillar.subtitle}</p>
        )}
        {isComingSoon && (
          <p className="text-[10px] text-white/35 mt-0.5 italic">Coming Soon</p>
        )}
      </motion.div>
    </motion.button>
  );
};

// =============================================================
// EXCELIOR ORB COMPONENT
// =============================================================

const ExceliorOrb = ({ onClick, whisper }) => {
  return (
    <motion.div
      className="absolute bottom-24 right-6 z-30"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1.5, type: 'spring' }}
    >
      {/* Whisper text */}
      <AnimatePresence>
        {whisper && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap"
          >
            <p className="text-xs text-white/40 italic max-w-[180px] text-right leading-relaxed">
              {whisper}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      <button
        onClick={onClick}
        className="relative group"
        data-testid="excelior-orb"
      >
        {/* Outer breathing pulse */}
        <motion.div 
          className="absolute inset-0 rounded-full"
          style={{
            width: 56,
            height: 56,
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)'
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.1, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Inner glow */}
        <motion.div 
          className="absolute inset-0 rounded-full blur-md"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(236, 72, 153, 0.4) 100%)'
          }}
          animate={{
            opacity: [0.4, 0.6, 0.4]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Orb */}
        <motion.div 
          className="relative w-14 h-14 rounded-full flex items-center justify-center
                     border border-white/20 group-hover:border-white/30 transition-colors"
          style={{
            background: `radial-gradient(circle at 30% 30%, 
              rgba(139, 92, 246, 0.3) 0%, 
              rgba(236, 72, 153, 0.2) 50%,
              rgba(0,0,0,0.2) 100%)`
          }}
          animate={{
            y: [0, -2, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <MessageCircle className="w-5 h-5 text-white/70 group-hover:text-white/90 transition-colors" />
        </motion.div>
        
        {/* Hover label */}
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap
                        opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <p className="text-xs text-white/50">Guide me</p>
        </div>
      </button>
    </motion.div>
  );
};

// =============================================================
// MAIN COMPONENT
// =============================================================

const ReclaimMapPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [whisper, setWhisper] = useState(null);
  const [highlightedPillar, setHighlightedPillar] = useState(null);
  const introCheckedRef = useRef(false);
  const whisperIntervalRef = useRef(null);

  // Check for first visit
  useEffect(() => {
    if (introCheckedRef.current) return;
    introCheckedRef.current = true;
    
    const hasSeenIntro = localStorage.getItem('reclaimMapIntroSeen');
    if (!hasSeenIntro) {
      setShowIntro(true);
    }
  }, []);

  // Whisper timer
  useEffect(() => {
    if (showIntro || selectedPillar || showGuide) {
      setWhisper(null);
      return;
    }
    
    // Show occasional whisper
    whisperIntervalRef.current = setInterval(() => {
      if (Math.random() < 0.3) {
        setWhisper(WHISPERS[Math.floor(Math.random() * WHISPERS.length)]);
        setTimeout(() => setWhisper(null), 5000);
      }
    }, 12000);
    
    return () => clearInterval(whisperIntervalRef.current);
  }, [showIntro, selectedPillar, showGuide]);

  const dismissIntro = () => {
    setShowIntro(false);
    localStorage.setItem('reclaimMapIntroSeen', 'true');
  };

  const handlePillarSelect = (pillar) => {
    if (selectedPillar?.id === pillar.id) {
      setSelectedPillar(null);
    } else {
      setSelectedPillar(pillar);
      setWhisper(null);
    }
  };

  const handleFeelingSelect = async (feeling) => {
    setShowGuide(false);
    
    if (feeling.id === 'curious') {
      return;
    }
    
    if (feeling.pillar) {
      const pillar = PILLARS.find(p => p.id === feeling.pillar);
      if (pillar) {
        setHighlightedPillar(feeling.pillar);
        setSelectedPillar(pillar);
      }
    }
  };

  const enterPillar = (pillar) => {
    if (pillar.status === 'active') {
      navigate(pillar.route);
    }
  };

  const isFocusMode = !!selectedPillar;

  return (
    <div className="fixed inset-0 overflow-hidden">
      {/* Premium celestial background with reduced saturation */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 20%, rgba(59, 130, 246, 0.04) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(147, 51, 234, 0.04) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%),
            linear-gradient(180deg, #080810 0%, #0a0a14 50%, #080810 100%)
          `
        }}
      />
      
      {/* Nebula layer - soft flowing gradients */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full"
          style={{
            left: '10%',
            top: '20%',
            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.03) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
          animate={{
            x: [-20, 20, -20],
            y: [-15, 15, -15],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full"
          style={{
            right: '15%',
            bottom: '25%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.025) 0%, transparent 70%)',
            filter: 'blur(70px)'
          }}
          animate={{
            x: [15, -15, 15],
            y: [10, -10, 10],
            scale: [1.05, 0.95, 1.05]
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.04) 0%, transparent 60%)',
            filter: 'blur(60px)'
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      
      {/* Celestial particles - stars, floating dust, node auras */}
      <CelestialBackground pillars={PILLARS} />
      
      {/* Dim overlay when in focus mode */}
      <AnimatePresence>
        {isFocusMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 z-5"
            onClick={() => setSelectedPillar(null)}
          />
        )}
      </AnimatePresence>
      
      {/* Connection lines - faint, only brighten on hover/selection */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
        <defs>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(147, 51, 234, 0.08)" />
            <stop offset="50%" stopColor="rgba(147, 51, 234, 0.15)" />
            <stop offset="100%" stopColor="rgba(147, 51, 234, 0.08)" />
          </linearGradient>
          <linearGradient id="connectionGradientActive" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(147, 51, 234, 0.2)" />
            <stop offset="50%" stopColor="rgba(147, 51, 234, 0.4)" />
            <stop offset="100%" stopColor="rgba(147, 51, 234, 0.2)" />
          </linearGradient>
        </defs>
        
        {/* Vertical flow: Balance → Peace → Private Suite → Well Being → Liberation */}
        <motion.line x1="38%" y1="14%" x2="38%" y2="28%" 
          stroke={selectedPillar?.id === 'peace' || selectedPillar?.id === 'balance' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'peace' || selectedPillar?.id === 'balance' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'peace' || selectedPillar?.id === 'balance' ? 0.6 : 0.12 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="28%" x2="38%" y2="42%" 
          stroke={selectedPillar?.id === 'peace' || selectedPillar?.id === 'private-suite' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'peace' || selectedPillar?.id === 'private-suite' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'peace' || selectedPillar?.id === 'private-suite' ? 0.6 : 0.12 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="42%" x2="38%" y2="60%" 
          stroke={selectedPillar?.id === 'wellbeing' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'wellbeing' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'wellbeing' ? 0.8 : 0.1 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="60%" x2="38%" y2="76%" stroke="url(#connectionGradient)" strokeWidth="1"
          animate={{ opacity: selectedPillar?.id === 'liberation' ? 0.6 : 0.05 }} 
          transition={{ duration: 0.3 }}
        />
        
        {/* Lines from center to left side */}
        <motion.line x1="38%" y1="42%" x2="14%" y2="28%" 
          stroke={selectedPillar?.id === 'passion' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'passion' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'passion' ? 0.8 : 0.15 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="42%" x2="10%" y2="46%" 
          stroke={selectedPillar?.id === 'abundance' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'abundance' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'abundance' ? 0.8 : 0.1 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="60%" x2="8%" y2="66%" stroke="url(#connectionGradient)" strokeWidth="1"
          animate={{ opacity: selectedPillar?.id === 'radiance' ? 0.6 : 0.05 }} 
          transition={{ duration: 0.3 }}
        />
        
        {/* Lines from center to right side */}
        <motion.line x1="38%" y1="42%" x2="62%" y2="22%" 
          stroke={selectedPillar?.id === 'studio' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'studio' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'studio' ? 0.8 : 0.15 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="42%" x2="66%" y2="46%" 
          stroke={selectedPillar?.id === 'strength' ? "url(#connectionGradientActive)" : "url(#connectionGradient)"} 
          strokeWidth={selectedPillar?.id === 'strength' ? 1.5 : 1}
          animate={{ opacity: selectedPillar?.id === 'strength' ? 0.8 : 0.1 }} 
          transition={{ duration: 0.3 }}
        />
        <motion.line x1="38%" y1="60%" x2="68%" y2="66%" stroke="url(#connectionGradient)" strokeWidth="1"
          animate={{ opacity: selectedPillar?.id === 'alignment' ? 0.6 : 0.05 }} 
          transition={{ duration: 0.3 }}
        />
        
        {/* Zones - subtle connection from side, off main path */}
        <motion.line x1="66%" y1="46%" x2="78%" y2="42%" stroke="url(#connectionGradient)" strokeWidth="1"
          animate={{ opacity: selectedPillar?.id === 'zones' ? 0.5 : 0.03 }} 
          transition={{ duration: 0.3 }}
        />
      </svg>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-white/40 hover:text-white/70 transition-colors text-sm"
          >
            ← Back
          </button>
          <div className="text-center">
            <h1 className="text-white/60 text-sm tracking-[0.2em] uppercase font-light">
              The Reclaim Map
            </h1>
          </div>
          <div className="w-12" />
        </div>
      </div>

      {/* Pillar Nodes */}
      <div className="absolute inset-0" style={{ zIndex: 10 }}>
        {PILLARS.map((pillar) => (
          <MapNode
            key={pillar.id}
            pillar={pillar}
            isSelected={selectedPillar?.id === pillar.id}
            isHighlighted={highlightedPillar === pillar.id}
            onSelect={handlePillarSelect}
            isFocusMode={isFocusMode}
          />
        ))}
      </div>

      {/* Excelior Orb */}
      <ExceliorOrb 
        onClick={() => setShowGuide(true)}
        whisper={whisper}
      />

      {/* Intro Overlay */}
      <AnimatePresence>
        {showIntro && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-[#0a0a12]/98 flex items-center justify-center p-8"
            onClick={dismissIntro}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center max-w-sm"
            >
              <motion.div 
                className="w-16 h-16 mx-auto mb-8 rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)'
                }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <MessageCircle className="w-6 h-6 text-white/60" />
              </motion.div>
              
              <motion.div 
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <p className="text-white/80 text-xl leading-relaxed">
                  This is your ecosystem
                </p>
                <p className="text-white/50 text-base leading-relaxed">
                  Each space supports a different part of your return
                </p>
              </motion.div>
              
              <motion.div 
                className="mt-10 space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <p className="text-white/40 text-sm">You don't need to explore everything</p>
                <p className="text-white/40 text-sm">Just begin where you feel drawn</p>
              </motion.div>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="mt-12 text-white/20 text-xs"
              >
                Tap to continue
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Pillar Card */}
      <AnimatePresence>
        {selectedPillar && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25 }}
            className="absolute bottom-0 left-0 right-0 z-40 p-6 pb-8"
          >
            <div 
              className="max-w-md mx-auto rounded-3xl p-6 relative overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: `0 0 60px ${selectedPillar.haloColor}`
              }}
            >
              {/* Close button */}
              <button
                onClick={() => { setSelectedPillar(null); setHighlightedPillar(null); }}
                className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center relative"
                  style={{ 
                    background: `radial-gradient(circle, ${selectedPillar.glowColor} 0%, transparent 70%)`
                  }}
                >
                  <selectedPillar.icon 
                    className="w-6 h-6" 
                    style={{ color: selectedPillar.color }} 
                  />
                </div>
                <div>
                  <h3 className="text-white text-lg font-medium tracking-wide">
                    {selectedPillar.name}
                  </h3>
                  {selectedPillar.subtitle && (
                    <p className="text-white/40 text-sm">{selectedPillar.subtitle}</p>
                  )}
                </div>
              </div>
              
              {/* Body text */}
              <div className="mb-6 space-y-3">
                {selectedPillar.body.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-white/60 text-sm leading-relaxed">
                    {paragraph.split('\n').map((line, j) => (
                      <span key={j}>
                        {line}
                        {j < paragraph.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </p>
                ))}
              </div>
              
              {/* Excelior line */}
              {selectedPillar.exceliorLine && (
                <p className="text-white/30 text-xs italic mb-6">
                  "{selectedPillar.exceliorLine}"
                </p>
              )}
              
              {/* CTA */}
              {selectedPillar.status === 'active' ? (
                <motion.button
                  onClick={() => enterPillar(selectedPillar)}
                  className="w-full py-4 rounded-2xl text-white font-medium flex items-center justify-center gap-2
                           transition-all duration-300"
                  style={{ 
                    background: `linear-gradient(135deg, ${selectedPillar.color} 0%, ${selectedPillar.color}dd 100%)`,
                    boxShadow: `0 4px 20px ${selectedPillar.glowColor}`
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`enter-${selectedPillar.id}`}
                >
                  {selectedPillar.cta}
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              ) : selectedPillar.status === 'coming' ? (
                <div className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-white/40 
                               text-center text-sm">
                  Coming Soon
                </div>
              ) : (
                <div className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-white/40 
                               text-center text-sm">
                  {selectedPillar.status === 'invitation' ? 'Invitation Only' : 'Locked'}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guide Mode Overlay */}
      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-[#0a0a12]/95 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="rounded-3xl p-8 max-w-sm w-full relative"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            >
              <button
                onClick={() => setShowGuide(false)}
                className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="text-center mb-8">
                <motion.div 
                  className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)'
                  }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <MessageCircle className="w-5 h-5 text-white/60" />
                </motion.div>
                <p className="text-white/70 text-lg">
                  Tell me what you feel right now…
                </p>
              </div>
              
              <div className="space-y-3">
                {FEELINGS.map((feeling) => (
                  <button
                    key={feeling.id}
                    onClick={() => handleFeelingSelect(feeling)}
                    className="w-full py-4 px-5 rounded-xl bg-white/5 border border-white/10 
                             text-white/60 text-left hover:bg-white/10 hover:border-white/20 
                             hover:text-white/80 transition-all duration-300"
                    data-testid={`feeling-${feeling.id}`}
                  >
                    {feeling.label}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReclaimMapPage;
